<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core';
import { inject, onMounted, onUnmounted, ref, watch, watchEffect } from 'vue';
import visibleSectionsSymbol from './symbols';
import ProgressStateIcon from '@panel/components/app/ProgressStateIcon.vue';

const props = withDefaults(
  defineProps<{
    title: string;
    preTitle: string;
    sectionKey: string;
    isLoading?: boolean;
    isDone?: boolean;
    isInvalid?: boolean;
  }>(),
  {
    isDone: false,
    isLoading: false,
    isInvalid: false,
  },
);

const section = ref<HTMLElement>();
const sectionsMapping = inject(visibleSectionsSymbol, {});
const isIntersectingRef = ref(false);
const { stop } = useIntersectionObserver(
  section,
  ([{ isIntersecting }]) => {
    isIntersectingRef.value = isIntersecting;
  },
  { threshold: 0.5 },
);

const internalIsLoading = ref(false);
const internalIsDone = ref(false);

watchEffect(() => {
  if (section.value == null || sectionsMapping == null) return;

  sectionsMapping[props.sectionKey] = {
    ...sectionsMapping[props.sectionKey],
    isVisible: isIntersectingRef.value,
    isLoading: internalIsLoading.value,
    isDone: internalIsDone.value,
    isInvalid: props.isInvalid,
  };
});

/**
 * with this logic we want to throttle the state change in the UI, since the loading spinner should be shown,
 * if the loading takes too long. If `isDone` happen earlier, the loading state should be ignored.
 */
const timeoutId = ref<ReturnType<typeof setTimeout>>();
watch(
  () => props.isLoading,
  () => {
    if (props.isLoading) {
      timeoutId.value = setTimeout(() => {
        internalIsLoading.value = true;
      }, 1000);
    } else {
      internalIsLoading.value = false;
      if (timeoutId.value != null && timeoutId.value > 0) {
        clearTimeout(timeoutId.value);
      }
    }
  },
  { immediate: true },
);

watch(
  () => props.isDone,
  () => {
    internalIsDone.value = props.isDone;
    if (props.isDone) {
      internalIsLoading.value = false;
    }
  },
  { immediate: true },
);

onMounted(() => {
  if (sectionsMapping == null) return;
  sectionsMapping[props.sectionKey] = {
    ...sectionsMapping[props.sectionKey],
    isMounted: true,
  };
});

onUnmounted(() => {
  if (sectionsMapping == null) return;
  sectionsMapping[props.sectionKey] = {
    ...sectionsMapping[props.sectionKey],
    isMounted: false,
    isLoading: false,
    isDone: false,
    isInvalid: false,
  };

  stop();
});
</script>
<template>
  <section ref="section" class="tw-mt-4 lg:tw-mt-8" :id="sectionKey" :key="sectionKey">
    <div class="tw-mb-4 tw-flex tw-items-center">
      <ProgressStateIcon
        class="tw-mr-4"
        :is-done="internalIsDone"
        :is-loading="internalIsLoading"
        :is-invalid="isInvalid"
      />
      <div class="tw-grid tw-flex-1 tw-grid-cols-2">
        <div class="tw-col-start-1">
          <span>{{ preTitle }}</span>
          <h4>{{ title }}</h4>
        </div>
        <div class="tw-col-end-4"><slot name="buttons" /></div>
      </div>
    </div>

    <div class="tw-flex-1">
      <slot></slot>
    </div>
  </section>
</template>
